/** Severity carried by contradiction/assumption/key_insight SSE events. */
type FindingSeverity = 'low' | 'medium' | 'high';

export type AgentStreamEventType =
  | 'status'
  | 'node_started'
  | 'node_completed'
  | 'tool_call'
  | 'token'
  | 'result'
  | 'error'
  | 'contradiction'
  | 'assumption'
  | 'key_insight'
  | 'artifact'
  | 'clarification'
  | 'question'
  | 'finding_status_changed'
  | 'verdict_recomputed'
  | 'scope_suggestions';

export interface StatusEvent {
  type: 'status';
  /** `label` is the AI-supplied human-readable string; when present the UI uses
   *  it verbatim in the ThinkingPanel. Absent for older backends → the client
   *  falls back to humanising the raw `phase` id. */
  payload: { phase: string; label?: string };
}

export interface NodeStartedEvent {
  type: 'node_started';
  payload: { node_id: string; label?: string };
}

/**
 * Emitted when the AI finishes a node. `label` — when present and
 * non-empty — is a summary of what that node did (e.g. "Dispatched 1 super
 * agent"). Empty/null labels are informational only and are dropped by the
 * UI so the thinking trail doesn't fill with duplicates of the start line.
 */
export interface NodeCompletedEvent {
  type: 'node_completed';
  payload: { node_id: string; label?: string | null };
}

export interface ToolCallEvent {
  type: 'tool_call';
  payload: { tool_name: string; label?: string };
}

export interface TokenEvent {
  type: 'token';
  payload: { text: string };
}

export interface ResultEvent {
  type: 'result';
  payload: { content: string };
}

export interface ErrorEvent {
  type: 'error';
  payload: { message: string };
}

export interface ContradictionEvent {
  type: 'contradiction';
  payload: {
    content: string;
    super_agent_id: string;
    confidence: number;
    severity: FindingSeverity;
    side_a: string;
    side_b: string;
  };
}

export interface AssumptionEvent {
  type: 'assumption';
  payload: {
    content: string;
    super_agent_id: string;
    confidence: number;
    severity: FindingSeverity;
  };
}

export interface KeyInsightEvent {
  type: 'key_insight';
  payload: {
    content: string;
    super_agent_id: string;
    confidence: number;
    severity: FindingSeverity;
  };
}

export type FindingEvent = ContradictionEvent | AssumptionEvent | KeyInsightEvent;

export interface ArtifactEvent {
  type: 'artifact';
  payload: {
    artifact_name: string;
    super_agent_id: string;
    // For `format: "json"` this is a JSON string (parse it); for markdown it's
    // the rendered text.
    content?: string | null;
    format?: 'markdown' | 'json' | null;
    source_tool_id?: string | null;
  };
}

export interface ClarificationEvent {
  type: 'clarification';
  payload: {
    question: string;
    // Deprecated: choices now come via the `question` event. Kept optional so
    // old payloads don't break typing; do not render this.
    options?: string[];
    correlation_id: string;
  };
}

/** How to render a question (radios / checkboxes / text input). */
export type QuestionKind = 'single_select' | 'multi_select' | 'free_text';

/** One selectable option within a question. */
export interface QuestionOption {
  id: string;
  label: string;
  /** One-line description of the option. */
  description?: string;
  /** Provenance, e.g. `web_search`, `uploaded_file`, `prior_conversation`. */
  source?: string;
}

/** One question within a set. */
export interface Question {
  question_id: string;
  question: string;
  kind: QuestionKind;
  required: boolean;
  options: QuestionOption[];
  min_selections: number;
  max_selections: number;
  /** When true, render an "other, please specify" free-text box. */
  allow_custom?: boolean;
  custom_label?: string;
}

/**
 * Interactive question set — the agent offering specific choices (with stable
 * ids) for the founder to pick from. Answered by sending back the ids. Distinct
 * from `clarification` (open-ended, typed in the chat box). At most one per turn.
 */
export interface QuestionEvent {
  type: 'question';
  payload: {
    question_set_id: string;
    asked_by: string; // atlas | huem | buildo | orga
    /** Deprecated mirror of `question_set_id`. */
    correlation_id?: string;
    questions: Question[];
  };
}

export type QuestionSetPayload = QuestionEvent['payload'];

export type FindingLifecycleType =
  | 'scope_readiness'
  | 'readiness_finding'
  | 'key_insight'
  | 'contradiction'
  | 'assumption'
  | 'strategy_alignment';

export interface FindingStatusChangedEvent {
  type: 'finding_status_changed';
  payload: {
    finding_id: string;
    scope_id: string;
    finding_type: FindingLifecycleType;
    status: string;
    previous_status: string | null;
    resolution_actor: 'user' | 'system' | 'write_back' | null;
  };
}

export interface VerdictRecomputedEvent {
  type: 'verdict_recomputed';
  payload: {
    scope_id: string;
    color: 'green' | 'amber' | 'red';
    status_line: string;
    reviewed_count: number;
  };
}

/**
 * Emitted on the response node once a prototype tool has produced a
 * shippable prototype for this turn. Replaces the old `X-Prototyper: true`
 * response header — the FE now discovers the "this turn is a prototyper
 * turn" signal from the SSE stream instead of the HTTP headers.
 *
 * `super_agent_id` identifies which super-agent owns the prototype (e.g.
 * "buildo").
 */
export interface PrototypeReadyEvent {
  type: 'prototype_ready';
  payload: {
    super_agent_id: string;
    /** Buildo's unversioned product name for the pill; absent on an older AI API. */
    prototype_title?: string | null;
  };
}

/** One AI-suggested scope (bulk scope suggestion from uploaded files). */
export interface SuggestedScope {
  name: string;
  description: string;
}

export interface ScopeSuggestionsEvent {
  type: 'scope_suggestions';
  payload: { scopes: SuggestedScope[] };
}

export type AgentStreamEvent =
  | StatusEvent
  | NodeStartedEvent
  | NodeCompletedEvent
  | ToolCallEvent
  | TokenEvent
  | ResultEvent
  | ErrorEvent
  | ContradictionEvent
  | AssumptionEvent
  | KeyInsightEvent
  | ArtifactEvent
  | ClarificationEvent
  | QuestionEvent
  | FindingStatusChangedEvent
  | VerdictRecomputedEvent
  | PrototypeReadyEvent
  | ScopeSuggestionsEvent;

export const isAgentStreamEvent = (data: unknown): data is AgentStreamEvent => {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return typeof obj.type === 'string' && 'payload' in obj;
};

export const isTokenEvent = (e: AgentStreamEvent): e is TokenEvent => e.type === 'token';
export const isResultEvent = (e: AgentStreamEvent): e is ResultEvent => e.type === 'result';
export const isErrorEvent = (e: AgentStreamEvent): e is ErrorEvent => e.type === 'error';
export const isClarificationEvent = (e: AgentStreamEvent): e is ClarificationEvent => e.type === 'clarification';
export const isQuestionEvent = (e: AgentStreamEvent): e is QuestionEvent => e.type === 'question';
export const isArtifactEvent = (e: AgentStreamEvent): e is ArtifactEvent => e.type === 'artifact';
export const isFindingEvent = (e: AgentStreamEvent): e is FindingEvent =>
  e.type === 'contradiction' || e.type === 'assumption' || e.type === 'key_insight';
export const isFindingStatusChangedEvent = (
  e: AgentStreamEvent
): e is FindingStatusChangedEvent => e.type === 'finding_status_changed';
export const isVerdictRecomputedEvent = (e: AgentStreamEvent): e is VerdictRecomputedEvent =>
  e.type === 'verdict_recomputed';
